import React, { useState, useEffect } from 'react';
import { FormGroup, Button, MenuItem } from '@blueprintjs/core';
import { Select, ItemRenderer } from '@blueprintjs/select';
import { useField, MarkupProps, Markup, useFormContext } from 'blueprint-form';

import HighlightText from '../../atoms/text/highlight';
import {
  IContact,
  useGetContacts,
  GetContact,
  GetContactResponse
} from '../../../libs/contact';
import { ErrorResponse } from '../../../libs/request';

const _SelectContact = Select.ofType<IContact>();

const GetContactText = (contact: IContact): string => {
  let text = contact.displayName;
  if (contact.description) {
    text += ` (${contact.description})`;
  }
  if (contact.email) {
    text += ` - ${contact.email}`;
  }
  return text;
};

const renderContact: ItemRenderer<IContact> = (
  contact,
  { handleClick, modifiers, query }
) => {
  if (!modifiers.matchesPredicate) {
    return null;
  }
  return (
    <MenuItem
      active={modifiers.active}
      disabled={modifiers.disabled}
      key={contact.id}
      onClick={handleClick}
      text={HighlightText(GetContactText(contact), query)}
    />
  );
};

export interface SelectContactProps extends MarkupProps {
  id?: string;
  excludeIds?: number[];
}

const SelectContact = ({
  id,
  excludeIds,
  label,
  name,
  large,
  fill,
  required,
  bare
}: SelectContactProps): JSX.Element => {
  const { isDisabled } = useFormContext();
  // Set up the field and label ID
  const [field, meta, helpers] = useField<number>({
    name: name,
    validate: (value): string => {
      let error: string = null;
      if (required && !value) {
        error = 'You must select a contact!';
      }
      return error;
    }
  });
  id = id || Markup.getID();
  // Set up states
  const [keyword, setKeyword] = useState<string>(null);
  const [contact, setContact] = useState<IContact>(null);
  const buttonSize = large ? 'large' : 'medium';
  // For initial value, fetch the contact
  useEffect(() => {
    if (field.value && contact == null) {
      GetContact({
        id: field.value
      }).then((value) => {
        if ((value as GetContactResponse).contact) {
          setContact((value as GetContactResponse).contact);
        } else if (value as ErrorResponse) {
          // display error message
        }
      });
    }
  }, [field.value]);
  // Fetch contacts based on keyword
  const { contacts } = useGetContacts({
    page: 0,
    limit: 5,
    keyword: keyword,
    excludeIds: excludeIds
  });
  // Search handler
  const onSearch = (query: string) => {
    setKeyword(query);
    return;
  };
  // Selection handler
  const onSelect = (contact: IContact) => {
    setContact(contact);
    helpers.setTouched(true);
    helpers.setValue(contact.id);
    return;
  };
  const getInput = (): JSX.Element => (
    <>
      <_SelectContact
        popoverProps={{
          usePortal: false
        }}
        fill={fill}
        items={contacts || []}
        itemRenderer={renderContact}
        onItemSelect={onSelect}
        onQueryChange={onSearch}
        disabled={isDisabled}
      >
        <Button
          className="button-text-ellipsis"
          type="button"
          alignText="left"
          size={buttonSize}
          fill={fill}
          endIcon="caret-down"
          intent={meta.touched && meta.error ? 'danger' : 'none'}
        >
          <span>{contact ? GetContactText(contact) : '-- Select One --'}</span>
        </Button>
      </_SelectContact>
      <input id={id} type="hidden" name={name} value={String(field.value)} />
    </>
  );
  if (bare) {
    return getInput();
  }
  return (
    <FormGroup
      label={label}
      labelFor={id}
      labelInfo={required ? '(required)' : null}
      intent={meta.touched && meta.error ? 'danger' : 'none'}
      helperText={meta.touched && meta.error ? meta.error : null}
    >
      {getInput()}
    </FormGroup>
  );
};

export default SelectContact;
